import { Button, Form, message, Table, TableColumnsType, Typography } from 'antd'
import { StakingNftType } from 'Modules'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { StatusStakingNFTEnum } from 'constants/staking-nft'
import ModalStakingNft from 'modules/stake-nft/listing/modal-staking-nft'
import Overview from 'modules/stake-nft/listing/overview'
import { PAGES, ROUTES } from 'routes'
import { convertDateToString } from 'utils/format-time'
import { logError } from 'utils/logger'

import './style.scss'

const { Text } = Typography

const StakingNFT = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [dataStaking, setDataStaking] = useState<Array<StakingNftType>>([])

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const [total, setTotal] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [messageApi, contextHolder] = message.useMessage()

  useEffect(() => {
    getDataStaking()
  }, [pagination])

  const getDataStaking = async () => {
    try {
      const res = await AppAdmin.getListStakingTokenize()
      if (res?.data.success === true) {
        const { results, totalResults } = res?.data?.data || {}
        setDataStaking(
          results?.map((item: StakingNftType) => ({
            key: item._id,
            id: item._id,
            name: item.name,
            contractAddress: item.contractAddress,
            status: item.isActive ? StatusStakingNFTEnum.ENABLE : StatusStakingNFTEnum.DISABLE,
            isActive: item.isActive,
            createdAt: item.createdAt,
          }))
        )
        setTotal(totalResults)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const showEditModal = async (record: any) => {
    setIsModalOpen(true)
    setModalMode('update')
    form.setFieldsValue(record)
  }

  const handleClickCreate = () => {
    form.resetFields() // Clear form fields
    setModalMode('create') // Set mode to create
    setIsModalOpen(true)
  }

  const afterOnSuccess = async (response: any) => {
    setIsModalOpen(false)
    messageApi.open({
      type: 'success',
      content: response.data.message,
    })
    await getDataStaking()
  }

  const columns: TableColumnsType<any> = [
    {
      title: 'Pool Type',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Button
          type="link"
          block
          color="primary"
          className="button-pool-name"
          onClick={() => {
            navigate(`${PAGES.STAKING_NFT}/${record?.id}`)
          }}
        >
          <Text style={{ width: 150 }} ellipsis={{ tooltip: text }} className="text-pool-name">
            {text}
          </Text>
        </Button>
      ),
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <LinkAddress address={text} type="address" />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status - b.status,
      render: (status) =>
        status ? (
          <span className="status-success">Enable</span>
        ) : (
          <span className="status-fail">Disable</span>
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => a.createdAt - b.createdAt,
      render: (text: any, record: any) => <span>{convertDateToString(record.createdAt)}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <div className="btn-group">
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`${ROUTES.STAKING_NFT}/${record?.id}/pto`)
            }}
          >
            List PTO
          </Button>

          <ProtectedButton
            className="button-edit"
            feature={FEATURE.STAKING_NFT} requireCRUD
            onClick={() => showEditModal(record)}
          >
            Edit
          </ProtectedButton>
        </div>
      ),
    },
  ]

  return (
    <>
      {contextHolder}
      <div className="main-section staking">
        <Breadcrumb additionalItems={[BREADCRUMBS.STAKING_TOKEN]} />
        <div className="dashboard-container">
          <Overview />
        </div>
        <div className="main-title">
          Staking NFTs
          <span className="new-button">
            <ProtectedButton
              type="primary"
              feature={FEATURE.STAKING_NFT} requireCRUD
              onClick={handleClickCreate}
            >
              New Pool
            </ProtectedButton>
          </span>
        </div>

        <div className="table-row">
          <div className="main-table-row">
            <Table<any>
              columns={columns}
              dataSource={dataStaking}
              className="main-table"
              scroll={{ x: 1000 }}
              pagination={
                total <= 10
                  ? false
                  : {
                      current: pagination.pageIndex + 1,
                      pageSize: pagination.pageSize,
                      pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                      onShowSizeChange(current, pageSize) {
                        setPagination({
                          pageIndex: current - 1,
                          pageSize,
                        })
                      },
                      onChange(page, pageSize) {
                        setPagination({
                          pageIndex: page - 1,
                          pageSize,
                        })
                      },
                    }
              }
            />
            <ModalStakingNft
              form={form}
              isModalOpen={isModalOpen}
              modalMode={modalMode}
              messageApi={messageApi}
              setIsModalOpen={setIsModalOpen}
              afterOnSuccess={afterOnSuccess}
            />
          </div>
        </div>
      </div>
    </>
  )
}
export default StakingNFT
